import { event } from '@/lib/google-analytics';
import { bookingAttractionsCityLink, bookingCityLink } from '@/lib/utils';
import { MapIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { MdHotel } from 'react-icons/md';

export default function BookingCityBanner({
	location,
	className,
	locale,
	date,
}) {
	const { t } = useTranslation('common');
	const imageSrc = `${process.env.NEXT_PUBLIC_AWS_S3_BUCKET}/locations/images/large/${location.id}.jpg`;
	const imageSrcFallback = `/images/hotel-banner.jpg`;

	const hotelsLeadURL = bookingCityLink({
		locale: locale,
		cityName: location.longName,
		checkIn: date,
	});

	const attractionsLeadURL = bookingAttractionsCityLink({
		cityName: location.shortName,
		countryCode: location.countryCode,
		locale: locale,
		date,
	});

	const handleAttractionsClick = () => {
		event('attractions_banner_click', {
			label: location.longName,
			category: 'attractions_banner',
		});
	};

	const handleHotelsClick = () => {
		event('hotel_banner_click', {
			label: location.longName,
			category: 'hotel_banner',
		});
	};

	return (
		<div
			className={clsx(
				'overflow-hidden rounded-lg ring-1 ring-gray-200',
				className
			)}
		>
			<div className="relative aspect-video rounded-lg">
				<Image
					title={t('hotels.title', {
						destination: location.shortName,
					})}
					src={imageSrc}
					priority={true}
					sizes="100vh"
					onError={(e) => {
						e.target.id = imageSrcFallback;
						e.target.srcset = imageSrcFallback;
					}}
					className="h-auto object-cover object-center transition-transform duration-300 ease-in group-hover:scale-105"
					alt={t('hotels.title', {
						destination: location.shortName,
					})}
					fill
				/>
				<div className="absolute -bottom-4 left-4 z-20 rounded-full bg-white/50 px-4 py-2 text-sm font-semibold text-gray-800 backdrop-blur-sm">
					<MapIcon className="-mt-0.5 mr-1 inline-block h-5 w-5" />
					{location.longName}
				</div>
			</div>
			<div className="px-4 pb-4 pt-8">
				<h2 className="text-balance text-lg/6 font-semibold text-gray-900 group-hover:text-gray-600">
					{t('cities.title', {
						destination: location.shortName,
					})}
				</h2>
				<p className="text-sm/6 mt-4 text-gray-600">
					{t('cities.subtitle', {
						destination: location.shortName,
					})}
				</p>
				<div className="mt-4 flex flex-wrap items-center justify-between gap-4">
					<a
						onClick={() => handleHotelsClick()}
						href={hotelsLeadURL}
						target="_blank"
						rel="noreferrer noopener"
						className="flex flex-1 shrink-0 cursor-pointer items-center justify-center gap-x-1.5 rounded-full bg-white px-4 py-2.5 font-semibold text-black drop-shadow-md"
					>
						<MdHotel className="h-5 w-5" />
						{t('hotels.button')}
					</a>
				</div>
			</div>
		</div>
	);
}
